import React from 'react';
import { Form } from 'react-bootstrap';
import { useLARequirements } from '@/features/applications/faculty/CreateApplicationSection3';
import ProgramCommitments from './ProgramCommitments';
import { hasError } from './shared';
import {
    LA_ERROR_MESSAGES,
    LA_IDS,
    LA_QUESTIONS,
    MAX_ESSAY_LENGTH,
    OTHER_ROLES,
    PRIOR_ROLES,
} from './laAnswers';
import useOtherRoleLinks from './useOtherRoleLinks';

// The Learning Assistant questions, in the order the LA program asked them.
// Props: { answers, onChange(field, value), errorIds, context } with context
// { term, isEditing, applicationId }. The new-or-returning and attendance
// questions are LaEligibility's.
function LaQuestions({ answers, onChange, errorIds, context = {} }) {
    const requirements = useLARequirements(context.term, true);
    const { isReturning, priorRoles, otherRoleInterest } = answers;
    const roleLinks = useOtherRoleLinks();
    const hoursLimit = context.weeklyHoursLimit;

    const toggleRole = (value, checked) => {
        let next;
        if (!checked) next = priorRoles.filter((role) => role !== value);
        else if (value === 'none') next = ['none'];
        else next = [...priorRoles.filter((role) => role !== 'none'), value];
        onChange('priorRoles', next);
    };

    // "No, just LA" stands alone, as "None of these" does above
    const toggleInterest = (value, checked) => {
        let next;
        if (!checked) next = otherRoleInterest.filter((role) => role !== value);
        else if (value === 'none') next = ['none'];
        else
            next = [
                ...otherRoleInterest.filter((role) => role !== 'none'),
                value,
            ];
        onChange('otherRoleInterest', next);
    };

    const rolesInvalid = hasError(errorIds, LA_IDS.priorRoles);
    const rolesErrorId = 'la-prior-roles-error';
    const rolesError =
        priorRoles.includes('none') && priorRoles.length > 1
            ? '"None of these" cannot be combined with another choice.'
            : LA_ERROR_MESSAGES[LA_IDS.priorRoles];

    const essay = (field, rows) => {
        const id = LA_IDS[field];
        const invalid = hasError(errorIds, id);
        return (
            <Form.Group className="mb-3" controlId={id}>
                <Form.Label>
                    {LA_QUESTIONS[field]}{' '}
                    <span className="text-danger" aria-hidden="true">
                        *
                    </span>
                </Form.Label>
                <Form.Control
                    as="textarea"
                    rows={rows}
                    maxLength={MAX_ESSAY_LENGTH}
                    value={answers[field]}
                    isInvalid={invalid}
                    aria-required="true"
                    aria-invalid={invalid ? 'true' : undefined}
                    aria-describedby={
                        invalid ? `${id}-error ${id}-help` : `${id}-help`
                    }
                    onChange={(e) => onChange(field, e.target.value)}
                />
                <Form.Text id={`${id}-help`} className="text-muted">
                    Up to {MAX_ESSAY_LENGTH.toLocaleString('en-US')} characters.
                </Form.Text>
                {invalid && (
                    <div
                        id={`${id}-error`}
                        role="alert"
                        className="invalid-feedback d-block"
                    >
                        {LA_ERROR_MESSAGES[id]}
                    </div>
                )}
            </Form.Group>
        );
    };

    return (
        <div>
            <h2 className="h5 mb-3">Learning Assistant Questions</h2>

            {essay('interestStatement', 4)}
            {essay('priorExperience', 4)}

            <fieldset
                className="mb-3"
                aria-describedby={rolesInvalid ? rolesErrorId : undefined}
            >
                <legend className="form-label fs-6">
                    {LA_QUESTIONS.priorRoles}{' '}
                    <span className="text-danger" aria-hidden="true">
                        *
                    </span>
                </legend>
                {PRIOR_ROLES.map(({ value, label }) => (
                    <Form.Check
                        key={value}
                        type="checkbox"
                        id={`la-prior-role-${value}`}
                        label={label}
                        checked={priorRoles.includes(value)}
                        isInvalid={rolesInvalid}
                        aria-required="true"
                        aria-invalid={rolesInvalid ? 'true' : undefined}
                        aria-describedby={
                            rolesInvalid ? rolesErrorId : undefined
                        }
                        onChange={(e) => toggleRole(value, e.target.checked)}
                    />
                ))}
                {rolesInvalid && (
                    <div
                        id={rolesErrorId}
                        role="alert"
                        className="invalid-feedback d-block"
                    >
                        {rolesError}
                    </div>
                )}
            </fieldset>

            <fieldset className="mb-3" aria-describedby="la-other-role-help">
                <legend className="form-label fs-6">
                    {LA_QUESTIONS.otherRoleInterest}
                </legend>
                {OTHER_ROLES.map(({ value, label }) => (
                    <div
                        key={value}
                        className="d-flex flex-wrap align-items-baseline column-gap-3"
                    >
                        <Form.Check
                            type="checkbox"
                            id={`la-other-role-${value}`}
                            label={label}
                            checked={otherRoleInterest.includes(value)}
                            onChange={(e) =>
                                toggleInterest(value, e.target.checked)
                            }
                        />
                        {roleLinks[value] && (
                            <a
                                href={roleLinks[value]}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="small"
                            >
                                Learn more about {label}
                                <span className="visually-hidden">
                                    {' '}
                                    (opens in a new tab)
                                </span>
                            </a>
                        )}
                    </div>
                ))}
                <Form.Text id="la-other-role-help" className="text-muted">
                    {typeof hoursLimit === 'number' &&
                        `Students can work up to ${hoursLimit} hours a week across all campus positions, so taking more than one role may not be possible. `}
                    This is only for the programs' information. It does not
                    change your LA application.
                </Form.Text>
            </fieldset>

            <ProgramCommitments
                id={LA_IDS.commitments}
                roleName="Learning Assistant"
                prompt={`Answer '${LA_QUESTIONS.isReturning}' in the Eligibility step to see the commitments that apply to you.`}
                requirements={requirements}
                isReturning={isReturning}
                checked={answers.commitmentsAcknowledged}
                onChange={(value) => onChange('commitmentsAcknowledged', value)}
                invalid={hasError(errorIds, LA_IDS.commitments)}
            />
        </div>
    );
}

export default LaQuestions;
